/**
 * Segmented loan amortization engine (pure — no Angular / no model imports).
 *
 * The historical `Loan.calculateLoanTable()` regenerates a single homogeneous
 * amortization schedule from a constant parameter set. It cannot represent any
 * mid-life event (early repayment, payment holiday, rate revision, refinancing).
 *
 * This engine introduces a *segmented* schedule: a base loan plus a chronological
 * list of events. It walks the timeline period by period, carrying the running
 * balance / rate / installment forward, applying each event at its effective
 * date. With an EMPTY event list it reproduces the legacy `fixed` schedule to the
 * bit (see the regression spec) — so it is a strict superset and existing loans
 * are never affected.
 *
 * It is deliberately primitive-only (numbers, strings, Dates) and takes the
 * amortization formula as an injected callback (`fixedRatePayment`) so it reuses
 * the EXACT same math as `LoanCalculatorService` while staying dependency-free
 * and trivially unit-testable.
 */

export type EngineLoanType = 'fixed' | 'fixedcapital' | 'interestonly';

export type EngineEventType =
  | 'early_repayment'      // partial early repayment (lump sum against capital)
  | 'closure'             // total payoff / sale / refinanced-out → loan ends
  | 'capital_suspension'  // payment holiday from effectiveDate..suspensionEndDate
  | 'rate_change'         // rate revision / renegotiation / variable→fixed
  | 'term_change';        // extend / shorten the term

export type EngineRepaymentMode = 'reduce_payment' | 'reduce_term';
export type EngineSuspensionScope = 'capital_only' | 'full';
export type EngineSuspensionAfter = 'extend' | 'catch_up';
export type EngineRateRecalc = 'payment' | 'term';

export interface EngineEvent {
  type: EngineEventType;
  effectiveDate: Date;
  // early_repayment
  amount?: number;
  repaymentMode?: EngineRepaymentMode;
  // capital_suspension
  suspensionEndDate?: Date;
  suspensionScope?: EngineSuspensionScope;
  suspensionAfter?: EngineSuspensionAfter;
  // rate_change (annual percentage, e.g. 2.4 for 2.4%)
  newRatePct?: number;
  rateRecalc?: EngineRateRecalc;
  // term_change (new TOTAL term in months counted from the loan start date)
  newNumberOfMonths?: number;
  // costs (Belgian context) booked as a fee on the event period
  indemnity?: number;
  fees?: number;
  // display marker carried back onto the affected table row (runtime only)
  label?: string;
}

/** Amortization-formula callback (mirrors LoanCalculatorService.calculateFixedRatePayment). */
export type FixedRatePaymentFn = (
  principal: number,
  ratePct: number,
  termPeriods: number,
  periodsPerYear: number
) => number;

export interface EngineInput {
  startDate: Date;
  nominalAmount: number;
  annualRatePct: number;     // e.g. 2.4 (NOT 0.024)
  numberOfMonths: number;
  periodsPerYear: number;    // 12 (monthly) | 4 (quarterly) | 1 (yearly)
  loanType: EngineLoanType;
  openingFees: number;
  mortageFee: number;        // (sic) matches the existing model field name
  recurringFees: number;
  recurringFeePeriod: number;
  events: EngineEvent[];
  fixedRatePayment: FixedRatePaymentFn;
}

export interface EngineEntry {
  period: number;
  date: Date;
  startingPrincipal: number;
  interest: number;
  principalRepayment: number;
  endingPrincipal: number;
  fees: number;
  totalCashout: number;
  /** Runtime-only marker for the UI (segment boundary). Not persisted. */
  event?: { type: EngineEventType; label: string };
  /** True for rows that fall inside a capital-suspension window. Runtime-only. */
  suspended?: boolean;
}

/** Below this many euros the balance is treated as cleared (absorbs float residue). */
const CLEARED = 0.005;

/** Add `n` payment periods to a date, honouring the payment frequency. */
function addPeriods(date: Date, periodsPerYear: number, n = 1): Date {
  const d = new Date(date);
  if (periodsPerYear === 12) d.setMonth(d.getMonth() + n);
  else if (periodsPerYear === 4) d.setMonth(d.getMonth() + 3 * n);
  else if (periodsPerYear === 1) d.setFullYear(d.getFullYear() + n);
  else d.setMonth(d.getMonth() + Math.round((12 / periodsPerYear) * n));
  return d;
}

/** Total scheduled payment periods for a month-expressed term. Mirrors legacy ceil(). */
function termToPeriods(numberOfMonths: number, periodsPerYear: number): number {
  return Math.ceil(numberOfMonths / (12 / periodsPerYear));
}

/**
 * Build the full amortization table for a base loan + ordered events.
 *
 * Determinism: a point-event is applied at the start of the first period whose
 * date reaches its effective date (an early repayment dated on a payment day
 * reduces the balance BEFORE that day's interest is computed).
 */
export function buildSegmentedSchedule(input: EngineInput): EngineEntry[] {
  const {
    startDate, nominalAmount, periodsPerYear, loanType,
    openingFees, mortageFee, recurringFees, recurringFeePeriod, fixedRatePayment
  } = input;

  // `fixedcapital` has no dedicated branch in the legacy per-period engine — it
  // amortizes as `fixed`. We preserve that exact behaviour here (changing it
  // would silently rewrite every existing fixed-capital table).
  const isInterestOnly = loanType === 'interestonly';

  let balance = nominalAmount;
  let ratePct = input.annualRatePct;
  let scheduledEnd = termToPeriods(input.numberOfMonths, periodsPerYear);
  const periodicRate = () => ratePct / 100 / periodsPerYear;

  // Installment is an EXPLICIT euro amount so "keep the same payment" events
  // (reduce-term, rate-change keep-payment) can freeze it while the rate/balance
  // change. Recomputed only by events that recalculate the payment.
  let installment = fixedRatePayment(nominalAmount, ratePct, scheduledEnd, periodsPerYear);

  const entries: EngineEntry[] = [];

  // Period 0 — opening costs only (identical to the legacy table).
  entries.push({
    period: 0,
    date: new Date(startDate),
    startingPrincipal: balance,
    interest: 0,
    principalRepayment: 0,
    endingPrincipal: balance,
    fees: mortageFee + openingFees,
    totalCashout: mortageFee + openingFees
  });

  // Point-events (everything except suspensions, which are handled via a window
  // check), sorted by date; a closure on the same day is processed last.
  const pending = input.events
    .filter(e => e.type !== 'capital_suspension' && !!e.effectiveDate)
    .sort((a, b) => a.effectiveDate.getTime() - b.effectiveDate.getTime() ||
      (a.type === 'closure' ? 1 : 0) - (b.type === 'closure' ? 1 : 0));

  const suspensions = input.events.filter(e =>
    e.type === 'capital_suspension' && !!e.effectiveDate && !!e.suspensionEndDate);

  const safetyCap = scheduledEnd * 4 + 24;
  let date = addPeriods(startDate, periodsPerYear, 1);
  let wasSuspended = false;
  const consumedCatchUp = new Set<EngineEvent>();

  for (let period = 1; period <= safetyCap; period++) {
    let appliedMarker: EngineEntry['event'] | undefined;

    // --- Apply point-events whose effective date has been reached ---
    let closed = false;
    while (pending.length && pending[0].effectiveDate.getTime() <= date.getTime()) {
      const ev = pending.shift() as EngineEvent;
      const remaining = Math.max(1, scheduledEnd - period + 1);
      if (ev.type === 'early_repayment') {
        balance = Math.max(0, balance - (ev.amount ?? 0));
        if (ev.repaymentMode !== 'reduce_term') {
          installment = fixedRatePayment(balance, ratePct, remaining, periodsPerYear);
        }
      } else if (ev.type === 'rate_change') {
        if (typeof ev.newRatePct === 'number') ratePct = ev.newRatePct;
        if (ev.rateRecalc !== 'term') {
          installment = fixedRatePayment(balance, ratePct, remaining, periodsPerYear);
        }
      } else if (ev.type === 'term_change') {
        if (typeof ev.newNumberOfMonths === 'number') {
          scheduledEnd = Math.max(period, termToPeriods(ev.newNumberOfMonths, periodsPerYear));
        }
        installment = fixedRatePayment(balance, ratePct, Math.max(1, scheduledEnd - period + 1), periodsPerYear);
      } else if (ev.type === 'closure') {
        closed = true;
      }
      appliedMarker = { type: ev.type, label: ev.label ?? ev.type };
      if (closed) {
        const fees = (ev.indemnity ?? 0) + (ev.fees ?? 0);
        entries.push({
          period,
          date: new Date(date),
          startingPrincipal: balance,
          interest: 0,
          principalRepayment: balance,
          endingPrincipal: 0,
          fees,
          totalCashout: balance + fees,
          event: appliedMarker
        });
        balance = 0;
        break;
      }
    }
    if (closed) break;

    // --- Capital-suspension window check for this period ---
    const susp = suspensions.find(e =>
      e.effectiveDate.getTime() <= date.getTime() &&
      date.getTime() < (e.suspensionEndDate as Date).getTime());
    const inSuspension = !!susp;

    // Just exited a catch-up suspension → re-amortize over the periods left so
    // the original end date is preserved (the installment rises).
    if (wasSuspended && !inSuspension) {
      const ended = suspensions.find(e =>
        e.suspensionAfter === 'catch_up' &&
        date.getTime() >= (e.suspensionEndDate as Date).getTime() &&
        !consumedCatchUp.has(e));
      if (ended) {
        consumedCatchUp.add(ended);
        installment = fixedRatePayment(balance, ratePct, Math.max(1, scheduledEnd - period + 1), periodsPerYear);
      }
    }

    const startingPrincipal = balance;
    let interest: number;
    let principal: number;
    let cashoutOverride: number | null = null;

    if (inSuspension) {
      if (susp!.suspensionScope === 'full') {
        // Full deferral: nothing paid; interest is capitalized into the balance.
        balance += balance * periodicRate();
        interest = 0;
        principal = 0;
        cashoutOverride = 0;
      } else {
        // Capital-only holiday: interest is paid, principal frozen.
        interest = balance * periodicRate();
        principal = 0;
      }
      // `extend`: a suspended period does not amortize → push the end date out.
      if (susp!.suspensionAfter === 'extend') scheduledEnd += 1;
    } else if (isInterestOnly) {
      interest = balance * periodicRate();
      principal = period >= scheduledEnd ? balance : 0;
      balance = Math.max(0, balance - principal);
    } else {
      interest = balance * periodicRate();
      principal = Math.min(installment - interest, balance);
      if (principal < 0) principal = 0;
      balance = Math.max(0, balance - principal);
    }

    const fees = (recurringFeePeriod > 0 && period % recurringFeePeriod === 0) ? recurringFees : 0;

    const entry: EngineEntry = {
      period,
      date: new Date(date),
      startingPrincipal,
      interest,
      principalRepayment: principal,
      endingPrincipal: balance,
      fees,
      totalCashout: cashoutOverride !== null ? cashoutOverride + fees : principal + interest + fees
    };
    if (inSuspension) {
      entry.suspended = true;
      if (!wasSuspended) entry.event = { type: 'capital_suspension', label: susp!.label ?? 'capital_suspension' };
    }
    if (appliedMarker) entry.event = appliedMarker;
    entries.push(entry);

    wasSuspended = inSuspension;

    // Stop conditions. A fixed loan ends when the balance clears — so the term
    // FLOATS past `scheduledEnd` for keep-payment events (rate rise / reduce-
    // payment) and ends EARLY for reduce-term / large prepayments. An interest-
    // only loan never amortizes, so it stops at the (event-adjusted) period
    // count. Both are bounded by the safety cap. We never stop mid-suspension.
    const moreSuspension = suspensions.some(e => (e.suspensionEndDate as Date).getTime() > date.getTime());
    if (!inSuspension && !moreSuspension) {
      if (isInterestOnly ? period >= scheduledEnd : balance <= CLEARED) break;
    }

    date = addPeriods(date, periodsPerYear, 1);
  }

  return entries;
}
